<script setup>
import OnboardingFeatureCard from './OnboardingFeatureCard.vue';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters } from 'dashboard/composables/store';

const getters = useStoreGetters();
const { t } = useI18n();
const globalConfig = computed(() => getters['globalConfig/get'].value);
const currentUser = computed(() => getters.getCurrentUser.value);

const greetingMessage = computed(() => {
  const hours = new Date().getHours();
  let translationKey;
  if (hours < 12) {
    translationKey = 'ONBOARDING.GREETING_MORNING';
  } else if (hours < 18) {
    translationKey = 'ONBOARDING.GREETING_AFTERNOON';
  } else {
    translationKey = 'ONBOARDING.GREETING_EVENING';
  }
  return t(translationKey, {
    name: currentUser.value.name,
    installationName: globalConfig.value.installationName,
  });
});

const firstName = computed(() => {
  return currentUser.value.name?.split(' ')[0] || '';
});
</script>

<template>
  <div
    class="min-h-screen max-w-3xl mx-auto flex flex-col gap-8 p-8 pt-12 w-full font-inter overflow-auto"
  >
    <!-- Header -->
    <div class="flex flex-col gap-3">
      <div class="flex items-center gap-3">
        <div
          class="size-10 rounded-xl bg-brand-gradient flex items-center justify-center shadow-sm"
        >
          <span class="i-ph-sparkle-fill text-white size-5" />
        </div>
        <div>
          <h1
            class="text-2xl font-bold text-n-stone-800 font-headline tracking-tight m-0"
          >
            {{ greetingMessage }}
          </h1>
        </div>
      </div>
      <p class="text-n-stone-400 text-[13px] leading-relaxed max-w-xl m-0">
        {{
          $t('ONBOARDING.DESCRIPTION', {
            installationName: globalConfig.installationName,
          })
        }}
      </p>
    </div>

    <!-- Cards grid -->
    <div class="grid grid-cols-2 gap-4">
      <OnboardingFeatureCard
        icon="i-lucide-inbox"
        icon-color="text-n-brand"
        icon-bg="bg-n-brand-50"
        to="settings_inbox_new"
        :title="$t('ONBOARDING.ALL_CONVERSATION.TITLE')"
        :description="$t('ONBOARDING.ALL_CONVERSATION.DESCRIPTION')"
        :link-text="$t('ONBOARDING.ALL_CONVERSATION.NEW_LINK')"
      />
      <OnboardingFeatureCard
        icon="i-lucide-users"
        icon-color="text-violet-600"
        icon-bg="bg-violet-50"
        to="settings_teams_new"
        :title="$t('ONBOARDING.TEAM_MEMBERS.TITLE')"
        :description="$t('ONBOARDING.TEAM_MEMBERS.DESCRIPTION')"
        :link-text="$t('ONBOARDING.TEAM_MEMBERS.NEW_LINK')"
      />
      <OnboardingFeatureCard
        icon="i-lucide-message-square-text"
        icon-color="text-emerald-600"
        icon-bg="bg-emerald-50"
        to="canned_list"
        :title="$t('ONBOARDING.CANNED_RESPONSES.TITLE')"
        :description="$t('ONBOARDING.CANNED_RESPONSES.DESCRIPTION')"
        :link-text="$t('ONBOARDING.CANNED_RESPONSES.NEW_LINK')"
      />
      <OnboardingFeatureCard
        icon="i-lucide-tags"
        icon-color="text-amber-600"
        icon-bg="bg-amber-50"
        to="labels_list"
        :title="$t('ONBOARDING.LABELS.TITLE')"
        :description="$t('ONBOARDING.LABELS.DESCRIPTION')"
        :link-text="$t('ONBOARDING.LABELS.NEW_LINK')"
      />
    </div>

    <!-- Keyboard shortcuts hint -->
    <div class="flex items-center justify-center gap-6 pt-2">
      <div class="flex items-center gap-2 text-n-stone-300 text-[11px]">
        <kbd
          class="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-white dark:bg-n-solid-3 border border-n-stone-200 rounded text-[10px] font-mono text-n-stone-400 shadow-sm"
        >
          <span class="text-[9px]">&#8984;</span>K
        </kbd>
        <span>Menu de comandos</span>
      </div>
      <div class="flex items-center gap-2 text-n-stone-300 text-[11px]">
        <kbd
          class="inline-flex items-center px-2 py-0.5 bg-white dark:bg-n-solid-3 border border-n-stone-200 rounded text-[10px] font-mono text-n-stone-400 shadow-sm"
        >
          /
        </kbd>
        <span>Atalhos de teclado</span>
      </div>
    </div>
  </div>
</template>
